/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { type CSSProperties } from 'react';
import { Link } from '@/components/link';
import {
    Carousel,
    CarouselContent,
    CarouselNext,
    CarouselPrevious,
    type CarouselApi,
} from '@/components/ui/carousel';
import { cn } from '@/lib/utils';
import HtmlFragment from '@/components/html-fragment';

export interface CarouselResponsiveSettings {
    mobileSlidesToShow?: string;
    mobileSlidesToScroll?: string;
    mobileShowArrows?: boolean;
    mobileShowDots?: boolean;
    tabletSlidesToShow?: string;
    tabletSlidesToScroll?: string;
    tabletShowArrows?: boolean;
    tabletShowDots?: boolean;
    desktopSlidesToShow?: string;
    desktopSlidesToScroll?: string;
    desktopShowArrows?: boolean;
    desktopShowDots?: boolean;
    loop?: boolean;
}

export interface CarouselSectionProps {
    title?: string;
    subtitle?: string;
    shopAllUrl?: string;
    shopAllText?: string;
    titleClassName?: string;
    titleAlign?: 'left' | 'center';
    className?: string;
    carouselClassName?: string;
    sectionHeading?: string;
    ariaLabel?: string;
    showDots?: boolean;
    alwaysShowControls?: boolean;
    responsiveSettings?: CarouselResponsiveSettings;
    children: ReactNode;
}

const defaultLeftTitleClassName =
    'font-georama font-bold text-[20px] leading-[110%] uppercase text-black md:font-sans md:text-4xl md:font-medium md:leading-none md:tracking-[-0.9px] md:normal-case';
const defaultCenterTitleClassName = 'text-4xl font-medium leading-none tracking-[-0.9px] text-black mb-4';

function getSlideWidth(value: string | undefined, gap: number, fallback: number): string {
    const slidesToShow = Math.max(1, Number(value) || fallback);
    const totalGap = gap * (slidesToShow - 1);
    return `calc((100% - ${totalGap}px) / ${slidesToShow})`;
}

// eslint-disable-next-line react-refresh/only-export-components
export { carouselItemImageWidths, productCarouselItemAspectRatio } from './constants';

/**
 * CarouselSection is a shared layout shell for all carousel components in the storefront.
 *
 * It provides the consistent outer padding, title row, Embla carousel track, and prev/next
 * navigation buttons. Consumers render their own <CarouselItem> elements as children.
 *
 * @example
 * ```tsx
 * // Left-aligned title with shop-all link (product carousel style)
 * <CarouselSection title="Featured Products" shopAllUrl="/category/root" shopAllText="Shop all">
 *     {products.map(p => (
 *         <CarouselItem key={p.productId} className="basis-1/2 sm:basis-1/3 md:basis-1/4 pl-4 flex min-w-0">
 *             <ProductTile product={p} className="h-full w-full" />
 *         </CarouselItem>
 *     ))}
 * </CarouselSection>
 *
 * // Centered title with subtitle (category carousel style)
 * <CarouselSection title="Shop by Category" subtitle="Explore our collections" titleAlign="center">
 *     {categories.map(c => (
 *         <CarouselItem key={c.id} className="basis-1/2 sm:basis-1/3 md:basis-1/4 pl-4 flex min-w-0">
 *             <CategoryTile category={c} className="h-full w-full" />
 *         </CarouselItem>
 *     ))}
 * </CarouselSection>
 * ```
 */
export function CarouselSection({
    title,
    subtitle,
    shopAllUrl,
    shopAllText,
    titleClassName,
    titleAlign = 'left',
    className,
    carouselClassName,
    sectionHeading,
    ariaLabel,
    showDots = true,
    alwaysShowControls = false,
    responsiveSettings,
    children,
}: CarouselSectionProps) {
    const [api, setApi] = useState<CarouselApi | null>(null);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [snapCount, setSnapCount] = useState(0);

    const onSelect = useCallback(() => {
        if (!api) return;
        setCurrentSlide(api.selectedScrollSnap());
    }, [api]);

    const onReInit = useCallback(() => {
        if (!api) return;
        setSnapCount(api.scrollSnapList().length);
        setCurrentSlide(api.selectedScrollSnap());
    }, [api]);

    useEffect(() => {
        if (!api) return;

        onReInit();
        api.on('select', onSelect);
        api.on('reInit', onReInit);

        return () => {
            api.off('select', onSelect);
            api.off('reInit', onReInit);
        };
    }, [api, onSelect, onReInit]);

    const goToSlide = useCallback(
        (index: number) => {
            if (!api) return;
            api.scrollTo(index);
        },
        [api]
    );

    const cleanHeading = (sectionHeading || title || '').replace(/<[^>]*>/g, '').trim();
    const isTrendingProduct = /trending(\s*products?)?/i.test(cleanHeading);
    const isNewArrival = /new\s*arrivals?/i.test(cleanHeading);
    const isRecommendedProduct =
        /recommend(ed|ations?)?(\s*products?)?|similar(\s*products?)?|you\s*might\s*also\s*like|you\s*may\s*also\s*like/i.test(cleanHeading);
    const isRideOns = /ride[\s-]*ons?(\s*from\s*mahindra)?/i.test(cleanHeading);

    const settings = {
        mobileSlidesToShow: '1', mobileSlidesToScroll: '1', tabletSlidesToShow: '3',
        tabletSlidesToScroll: '1', desktopSlidesToShow: '4', desktopSlidesToScroll: '1',
        mobileShowArrows: true, mobileShowDots: true, tabletShowArrows: true, tabletShowDots: true,
        desktopShowArrows: true, desktopShowDots: true, loop: false, ...responsiveSettings,
    };

    if (isNewArrival || isTrendingProduct || isRideOns || (settings.desktopShowDots && responsiveSettings?.mobileShowDots === undefined)) {
        settings.mobileShowDots = true;
    }
    const useResponsiveLayout = Boolean(responsiveSettings);
    const visibilityClass = (mobile: boolean, tablet: boolean, desktop: boolean) =>
        cn(mobile ? 'flex' : 'hidden', tablet ? 'md:max-lg:flex' : 'md:max-lg:hidden', desktop ? 'lg:flex' : 'lg:hidden');
    const carouselStyle = {
        '--carousel-mobile-gap': '4px',
        '--carousel-tablet-gap': '4px',
        '--carousel-desktop-gap': '4px',
        '--carousel-mobile-slide-width': getSlideWidth(settings.mobileSlidesToShow, 16, 1),
        '--carousel-tablet-slide-width': getSlideWidth(settings.tabletSlidesToShow, 24, 3),
        '--carousel-desktop-slide-width': getSlideWidth(settings.desktopSlidesToShow, 24, 4),
    } as CSSProperties;
    const titleSection =
        titleAlign === 'center' ? (
            <div className="text-center">
                {title && <h2 className={titleClassName ?? defaultCenterTitleClassName}>{title}</h2>}
                {subtitle && (
                    <p className="text-base font-normal leading-6 text-muted-foreground max-w-2xl mx-auto">
                        {subtitle}
                    </p>
                )}
            </div>
        ) : (
            <div className={cn(
                'flex items-center justify-between heading-section',
                (isTrendingProduct || isNewArrival || isRideOns || isRecommendedProduct) && 'pr-4 md:pr-6 lg:pr-16'
            )}>
                {subtitle ? (
                    <div>
                        {title && <h2 className={cn('!mt-0 !pt-0', titleClassName ?? defaultLeftTitleClassName)}>{title}</h2>}
                        <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
                    </div>
                ) : (
                    title && <h2 className={cn('!mt-0 !pt-0', titleClassName ?? defaultLeftTitleClassName)}>{title}</h2>
                )}
                {shopAllText && (
                    <div className="ml-4 shrink-0">
                        {shopAllUrl ? (
                            <Link
                                to={shopAllUrl}
                                className="shrink-0 font-lato text-base font-normal leading-5 text-foreground underline decoration-1 underline-offset-4 transition-colors hover:text-foreground/80">

                                {shopAllText}
                            </Link>
                        ) : (
                            <span className="shrink-0 font-lato text-base font-normal leading-5 text-foreground underline decoration-1 underline-offset-4">
                                <Link
                                    to={shopAllUrl}
                                    className="shrink-0 font-lato text-base font-normal leading-5 text-foreground underline decoration-1 underline-offset-4 transition-colors hover:text-foreground/80">
                                    {shopAllText}
                                </Link>
                                <span
                                    aria-hidden="true"
                                    className="h-px w-[58px] shrink-0 self-stretch bg-foreground"
                                />
                            </span>
                        )}
                    </div>
                )}
            </div>
        );

    const innerContent = (
        <>
            {sectionHeading ? (
                <HtmlFragment
                    content={sectionHeading}
                    contentType="plain-text"
                    className={cn(
                        'text-2xl carousel-heading font-medium',
                        isTrendingProduct ? 'md:pt-0' : 'md:pt-15',
                        (isTrendingProduct || isNewArrival || isRideOns || isRecommendedProduct) && 'pr-4 md:pr-6 lg:pr-16'
                    )}
                />
            ) : (
                (title !== undefined || subtitle) && titleSection
            )}
            <Carousel
                setApi={setApi}
                className="w-full pt-6"
                opts={
                    useResponsiveLayout
                        ? {
                            align: 'start',
                            loop: settings.loop,
                            slidesToScroll: Math.max(1, Number(settings.mobileSlidesToScroll) || 1),
                            breakpoints: {
                                '(min-width: 768px)': {
                                    axis: 'x',
                                    align: 'start',
                                    slidesToScroll: Math.max(1, Number(settings.tabletSlidesToScroll) || 1),
                                },
                                '(min-width: 1024px)': {
                                    axis: 'x',
                                    slidesToScroll: Math.max(1, Number(settings.desktopSlidesToScroll) || 1),
                                },
                            },
                        }
                        : { align: 'start' }
                }
                aria-label={ariaLabel}>
                <CarouselContent
                    className={cn(
                        useResponsiveLayout
                            ? [
                                'items-stretch flex-nowrap gap-[var(--carousel-mobile-gap)]',
                                'md:gap-[var(--carousel-tablet-gap)] lg:gap-[var(--carousel-desktop-gap)]',
                                '[&_[data-slot=carousel-item]]:!basis-[var(--carousel-mobile-slide-width)]',
                                'md:[&_[data-slot=carousel-item]]:!basis-[var(--carousel-tablet-slide-width)]',
                                'lg:[&_[data-slot=carousel-item]]:!basis-[var(--carousel-desktop-slide-width)]',
                            ]
                            : isRecommendedProduct
                                 ? '-ml-[4px] items-stretch flex-nowrap pr-4 md:pr-6 lg:pr-16'
                                : '-ml-4 items-stretch flex-nowrap'
                    )}
                    style={useResponsiveLayout ? ({} as CSSProperties) : undefined}>
                    {children}
                </CarouselContent>

                {(snapCount > 1 || alwaysShowControls) && (
                    <div
                        className={cn(
                            'mt-4 flex items-center justify-between',
                            isTrendingProduct && 'pr-4 md:pr-6 lg:pr-16',
                            isNewArrival && 'pr-4 md:pr-6 lg:pr-16',
                            isRecommendedProduct && 'pr-4 md:pr-6 lg:pr-16',
                            isRideOns && 'pr-4 md:pr-6 lg:!pr-[64px]'
                        )}>
                        {(useResponsiveLayout
                            ? (settings.mobileShowDots || settings.tabletShowDots || settings.desktopShowDots)
                            : showDots) && (
                                <div
                                    className={cn(
                                        'relative h-1 w-full max-w-30 items-center',
                                        visibilityClass(
                                            useResponsiveLayout ? settings.mobileShowDots : true,
                                            useResponsiveLayout ? settings.tabletShowDots : true,
                                            useResponsiveLayout ? settings.desktopShowDots : true
                                        )
                                    )}
                                    role="tablist"
                                    aria-label="Slide navigation">
                                    {/* continuous track */}
                                    <div className="absolute inset-0 rounded-full bg-foreground/15" />

                                    {/* active pill, positioned over the active segment */}
                                    <div
                                        className="absolute h-1 rounded-full bg-foreground transition-all duration-300"
                                        style={{
                                            left: `${(currentSlide / Math.max(1, snapCount)) * 100}%`,
                                            width: `${(1 / Math.max(1, snapCount)) * 100}%`,
                                        }}
                                    />

                                    {/* invisible clickable segments on top */}
                                    {Array.from({ length: Math.max(1, snapCount) }).map((_, index) => (
                                        <button
                                            key={`dot-${index}`}
                                            type="button"
                                            onClick={() => goToSlide(index)}
                                            className="relative z-10 h-full flex-1 focus:outline-none"
                                            role="tab"
                                            aria-selected={currentSlide === index}
                                            aria-label={`Go to slide ${index + 1} of ${Math.max(1, snapCount)}`}
                                            tabIndex={currentSlide === index ? 0 : -1}
                                        />
                                    ))}
                                </div>
                            )}

                        <div
                            className={cn(
                                'ml-auto gap-2',
                                visibilityClass(
                                    useResponsiveLayout ? settings.mobileShowArrows : true,
                                    useResponsiveLayout ? settings.tabletShowArrows : true,
                                    useResponsiveLayout ? settings.desktopShowArrows : true
                                )
                            )}>
                            <CarouselPrevious strokeWidth={1.5} variant="ghost" className="static flex size-9 translate-x-0 translate-y-0 rounded-full shadow-none bg-transparent hover:bg-transparent border-0 [&_svg]:[stroke-width:1.5px]" />
                            <CarouselNext strokeWidth={1.5} variant="ghost" className="static flex size-9 translate-x-0 translate-y-0 rounded-full shadow-none bg-transparent hover:bg-transparent border-0 [&_svg]:[stroke-width:1.5px]" />
                        </div>
                    </div>
                )}
            </Carousel>
        </>
    );

    if (isTrendingProduct) {
        return (
            <div
                data-slot="carousel-section"
                data-carousel-effect={useResponsiveLayout ? 'slide' : undefined}
                style={{
                    ...(useResponsiveLayout ? carouselStyle : undefined),
                    background: 'var(--neutral-13, #F5F5F5)',
                }}
                className="w-full bg-[var(--neutral-13,#F5F5F5)]">
                <div
                    className={cn(
                        'section-container pt-6 md:pt-10 pb-8 md:pb-12 !pr-0 lg:!pr-0',
                        className
                    )}>
                    {innerContent}
                </div>
            </div>
        );
    }

    if (isNewArrival) {
        return (
            <div
                data-slot="carousel-section"
                data-carousel-effect={useResponsiveLayout ? 'slide' : undefined}
                style={useResponsiveLayout ? carouselStyle : undefined}
                className={cn(
                    'section-container pt-10 md:pt-6 !pr-0 lg:!pr-0',
                    className
                )}>
                {innerContent}
            </div>
        );
    }

    if (isRecommendedProduct) {
        return (
            <div
                data-slot="carousel-section"
                data-carousel-effect={useResponsiveLayout ? 'slide' : undefined}
                style={useResponsiveLayout ? carouselStyle : undefined}
                className={cn(
                    'w-auto pt-10 md:pt-6 -mr-4 md:-mr-6 lg:-mr-16 overflow-hidden',
                    className
                )}>
                {innerContent}
            </div>
        );
    }

    if (isRideOns) {
        return (
            <div
                data-slot="carousel-section"
                data-carousel-effect={useResponsiveLayout ? 'slide' : undefined}
                style={useResponsiveLayout ? carouselStyle : undefined}
                className={cn(
                    'ride-ons-section section-container pt-10 md:pt-6 md:!pr-0 lg:!pr-0',
                    className
                )}>
                {innerContent}
            </div>
        );
    }

    return (
        <div
            data-slot="carousel-section"
            data-carousel-effect={useResponsiveLayout ? 'slide' : undefined}
            style={useResponsiveLayout ? carouselStyle : undefined}
            className={cn(
            'section-container pt-10 md:pt-6',
                className
            )}>
            {innerContent}
        </div>
    );
}
